import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import MobileSnapshotFab from '../../components/funnel/MobileSnapshotFab';
import type { ServiceDefinition } from '../../lib/pricebook/pricebook';
import FunnelShell from './FunnelShell';

type Option = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
};

type FormState = {
  readonly fullName: string;
  readonly email: string;
  readonly phone: string;
  readonly company: string;
  readonly preferredContact: 'email' | 'phone' | 'either';
  readonly requestDetails: string;
};

const PROJECT_TYPES: readonly Option[] = [
  { id: 'website', label: 'Website Rebuild', description: 'Large website redesign and rebuild.' },
  { id: 'ecommerce', label: 'Ecommerce Build', description: 'Store architecture, catalog, checkout, ops.' },
  { id: 'product', label: 'Product Sprint', description: 'Custom app features and workflow delivery.' },
  {
    id: 'branding_rebranding',
    label: 'Branding / Rebranding',
    description: 'Brand strategy, identity systems, and rebrand rollout execution.',
  },
  {
    id: 'vr_ar',
    label: 'VR / AR',
    description: 'Immersive experience design and development for spatial products.',
  },
  { id: 'migration', label: 'Migration/Integration', description: 'Platform migration and API integrations.' },
  {
    id: 'other',
    label: 'Other / All Other Types',
    description: 'Specialized requests that do not fit predefined categories.',
  },
];

const TIMELINES: readonly Option[] = [
  { id: 'urgent', label: 'Urgent (4-6 weeks)', description: 'Compressed delivery path.' },
  { id: 'standard', label: 'Standard (8-12 weeks)', description: 'Balanced planning + execution.' },
  { id: 'phased', label: 'Phased (12+ weeks)', description: 'Multi-phase initiative roadmap.' },
];

const ENGAGEMENTS: readonly Option[] = [
  { id: 'collab', label: 'Collaborative', description: 'Your team collaborates tightly with ours.' },
  { id: 'full', label: 'Full-Service', description: 'CyBard owns end-to-end delivery.' },
  { id: 'embedded', label: 'Embedded Support', description: 'We extend your internal team.' },
];

const STEP_ITEMS = Object.freeze([
  { step: 1, title: 'Choose Project Type' },
  { step: 2, title: 'Set Delivery Context' },
  { step: 3, title: 'Review Scope' },
  { step: 4, title: 'Submit Request' },
] as const);

const CONTACT_PREFERENCES = [
  {
    id: 'email',
    label: 'Email first',
    description: 'Best for detailed context and async follow-up.',
  },
  {
    id: 'phone',
    label: 'Phone first',
    description: 'Best for urgent or high-context discussions.',
  },
  {
    id: 'either',
    label: 'Either works',
    description: 'We will use the fastest available channel.',
  },
] as const;

const Layout = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) 280px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const LeftStepRail = styled.aside`
  border: none;
  border-radius: 16px;
  background: transparent;
  padding: 0.75rem;
  display: grid;
  gap: 0.65rem;
  position: fixed;
  left: 1.1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 176px;
  height: fit-content;
  z-index: 40;

  @media (max-width: 1360px) {
    left: 0.75rem;
    width: 164px;
  }

  @media (max-width: 1180px) {
    display: none;
  }
`;

const LeftStepList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.5rem;
`;

const LeftStepButton = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.gray200};
  border-radius: 12px;
  padding: 0.6rem;
  text-align: left;
  display: grid;
  gap: 0.14rem;
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.primary}14` : theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray900};
  cursor: pointer;
`;

const LeftStepLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray700};
`;

const LeftStepTitle = styled.span`
  font-size: 0.92rem;
  font-weight: 800;
`;

const Card = styled.section`
  border: none;
  border-radius: 16px;
  background: transparent;
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
  min-height: calc(100svh - 96px);
  scroll-margin-top: 88px;

  @media (max-width: 1080px) {
    min-height: auto;
  }
`;

const RightColumn = styled.aside`
  display: grid;
  gap: 0.75rem;
  align-self: start;
  position: sticky;
  top: 88px;
  height: fit-content;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 1080px) {
    display: grid;
    gap: 0.45rem;
  }
`;

const OptionGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const OptionButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray200)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.primary}14` : theme.colors.gray050)};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 10px;
  padding: 0.65rem;
  text-align: left;
  display: grid;
  gap: 0.2rem;
  cursor: pointer;
`;

const FormGrid = styled.form`
  display: grid;
  gap: 0.65rem;
`;

const Row = styled.div`
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Field = styled.label`
  display: grid;
  gap: 0.3rem;
  font-size: 0.86rem;
  font-weight: 700;
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.56rem 0.66rem;
  background: ${({ theme }) => theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray900};
`;

const Textarea = styled.textarea`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.56rem 0.66rem;
  min-height: 130px;
  resize: vertical;
  background: ${({ theme }) => theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray900};
`;

const PreferenceGrid = styled.div`
  display: grid;
  gap: 0.45rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const PreferenceButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray200)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.primary}14` : theme.colors.gray050)};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 10px;
  padding: 0.58rem;
  text-align: left;
  display: grid;
  gap: 0.12rem;
  cursor: pointer;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 0.2rem;
  color: ${({ theme }) => theme.colors.gray700};
`;

const PrimaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 10px;
  padding: 0.54rem 0.72rem;
  font-weight: 700;
  cursor: pointer;
`;

const GhostLink = styled(Link)`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 10px;
  padding: 0.54rem 0.72rem;
  font-weight: 700;
`;

const ErrorText = styled.span`
  color: #b91c1c;
  font-size: 0.78rem;
  font-weight: 700;
`;

const SuccessText = styled.div`
  border: 1px solid #16a34a;
  background: rgba(22, 163, 74, 0.12);
  color: #14532d;
  border-radius: 10px;
  padding: 0.6rem;
  font-weight: 700;
`;

function byId(options: readonly Option[], id: string): Option {
  return options.find((option) => option.id === id) || options[0];
}

function validEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function preferredContactLabel(value: FormState['preferredContact']): string {
  return CONTACT_PREFERENCES.find((option) => option.id === value)?.label || value;
}

type CustomProjectsServicePageProps = {
  readonly service: ServiceDefinition;
};

export default function CustomProjectsServicePage({ service }: CustomProjectsServicePageProps) {
  const [projectTypeId, setProjectTypeId] = useState(PROJECT_TYPES[0].id);
  const [timelineId, setTimelineId] = useState(TIMELINES[0].id);
  const [engagementId, setEngagementId] = useState(ENGAGEMENTS[0].id);
  const [form, setForm] = useState<FormState>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    preferredContact: 'email',
    requestDetails: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const stepSectionRefs = useRef<Record<number, HTMLElement | null>>({
    1: null,
    2: null,
    3: null,
    4: null,
  });
  const stepLockUntilRef = useRef<number>(0);
  const [activeStep, setActiveStep] = useState<number>(1);

  const selectedProjectType = useMemo(() => byId(PROJECT_TYPES, projectTypeId), [projectTypeId]);
  const selectedTimeline = useMemo(() => byId(TIMELINES, timelineId), [timelineId]);
  const selectedEngagement = useMemo(() => byId(ENGAGEMENTS, engagementId), [engagementId]);

  const quoteHref = useMemo(() => {
    const subject = encodeURIComponent(`Custom Project Consultation - ${selectedProjectType.label}`);
    const body = encodeURIComponent(
      [
        'Custom Project Request',
        '',
        `Name: ${form.fullName || '[not provided]'}`,
        `Company: ${form.company || '[not provided]'}`,
        `Email: ${form.email || '[not provided]'}`,
        `Phone: ${form.phone || '[not provided]'}`,
        `Preferred contact: ${form.preferredContact}`,
        '',
        `Project type: ${selectedProjectType.label}`,
        `Timeline: ${selectedTimeline.label}`,
        `Engagement: ${selectedEngagement.label}`,
        '',
        'Request details:',
        form.requestDetails || '[not provided]',
      ].join('\n'),
    );
    return `mailto:hello@cybard.co?subject=${subject}&body=${body}`;
  }, [form, selectedEngagement.label, selectedProjectType.label, selectedTimeline.label]);

  useEffect(() => {
    const updateActiveStep = () => {
      if (Date.now() < stepLockUntilRef.current) {
        return;
      }

      const triggerY = 120;
      let nextActive = 1;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const item of STEP_ITEMS) {
        const section = stepSectionRefs.current[item.step];
        if (!section) continue;
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top - triggerY);
        if (distance < bestDistance) {
          bestDistance = distance;
          nextActive = item.step;
        }
      }

      const nearPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 8;
      if (nearPageBottom) {
        nextActive = STEP_ITEMS[STEP_ITEMS.length - 1].step;
      }

      setActiveStep((current) => (current === nextActive ? current : nextActive));
    };

    updateActiveStep();
    window.addEventListener('scroll', updateActiveStep, { passive: true });
    window.addEventListener('resize', updateActiveStep);
    return () => {
      window.removeEventListener('scroll', updateActiveStep);
      window.removeEventListener('resize', updateActiveStep);
    };
  }, []);

  const setStepSectionRef =
    (step: number) =>
    (section: HTMLElement | null): void => {
      stepSectionRefs.current[step] = section;
    };

  const scrollToStep = (step: number) => {
    const section =
      stepSectionRefs.current[step] || document.getElementById(`custom-project-step-${step}`);
    if (!section) return;
    const headerOffset = 88;
    const sectionRect = section.getBoundingClientRect();
    const sectionTop = window.scrollY + sectionRect.top;
    const sectionBottom = window.scrollY + sectionRect.bottom;
    const maxScrollableTop =
      document.documentElement.scrollHeight - window.innerHeight;

    let targetTop = sectionTop - headerOffset;
    if (targetTop > maxScrollableTop) {
      targetTop = sectionBottom - window.innerHeight + 24;
    }

    stepLockUntilRef.current = Date.now() + 900;
    window.scrollTo({
      top: Math.max(0, Math.min(targetTop, maxScrollableTop)),
      behavior: 'smooth',
    });
    setActiveStep(step);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.fullName.trim()) nextErrors.fullName = 'Full name is required.';
    if (!form.email.trim()) nextErrors.email = 'Email is required.';
    else if (!validEmail(form.email)) nextErrors.email = 'Enter a valid email.';
    if (form.preferredContact === 'phone' && !form.phone.trim()) {
      nextErrors.phone = 'Phone is required when phone is preferred.';
    }
    if (!form.requestDetails.trim()) nextErrors.requestDetails = 'Request details are required.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSubmitted(true);
    window.location.href = quoteHref;
  };

  return (
    <FunnelShell>
      <Layout>
        <LeftStepRail aria-label="Custom Projects build steps">
          <LeftStepList>
            {STEP_ITEMS.map((item) => (
              <li key={item.step}>
                <LeftStepButton
                  type="button"
                  $active={activeStep === item.step}
                  onClick={() => scrollToStep(item.step)}
                >
                  <LeftStepLabel>{`Step ${item.step}`}</LeftStepLabel>
                  <LeftStepTitle>{item.title}</LeftStepTitle>
                </LeftStepButton>
              </li>
            ))}
          </LeftStepList>
        </LeftStepRail>

        <div style={{ display: 'grid', gap: '1rem' }}>
          <Card>
            <p style={{ margin: 0, fontWeight: 800 }}>Custom Projects</p>
            <h1 style={{ margin: 0 }}>{service.title}</h1>
            <p style={{ margin: 0 }}>
              This option exists for larger engagements or highly specific requests that do not
              cleanly fit our standard service plans.
            </p>
            <p style={{ margin: 0 }}>
              Submit your request below and we will review it, then contact you through your
              preferred channel to schedule a consultation.
            </p>
          </Card>

          <Card id="custom-project-step-1" ref={setStepSectionRef(1)}>
            <h2 style={{ margin: 0 }}>Step 1: Choose project type</h2>
            <OptionGrid>
              {PROJECT_TYPES.map((option) => (
                <OptionButton
                  key={option.id}
                  type="button"
                  $active={projectTypeId === option.id}
                  onClick={() => setProjectTypeId(option.id)}
                >
                  <strong>{option.label}</strong>
                  <span>{option.description}</span>
                </OptionButton>
              ))}
            </OptionGrid>
          </Card>

          <Card id="custom-project-step-2" ref={setStepSectionRef(2)}>
            <h2 style={{ margin: 0 }}>Step 2: Set delivery context</h2>
            <Row>
              <div>
                <strong>Timeline</strong>
                <OptionGrid>
                  {TIMELINES.map((option) => (
                    <OptionButton
                      key={option.id}
                      type="button"
                      $active={timelineId === option.id}
                      onClick={() => setTimelineId(option.id)}
                    >
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </OptionButton>
                  ))}
                </OptionGrid>
              </div>
              <div>
                <strong>Engagement model</strong>
                <OptionGrid>
                  {ENGAGEMENTS.map((option) => (
                    <OptionButton
                      key={option.id}
                      type="button"
                      $active={engagementId === option.id}
                      onClick={() => setEngagementId(option.id)}
                    >
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </OptionButton>
                  ))}
                </OptionGrid>
              </div>
            </Row>
          </Card>

          <Card id="custom-project-step-3" ref={setStepSectionRef(3)}>
            <h2 style={{ margin: 0 }}>Step 3: Review request scope</h2>
            <SummaryList>
              <li>Project type: {selectedProjectType.label}</li>
              <li>Timeline: {selectedTimeline.label}</li>
              <li>Engagement model: {selectedEngagement.label}</li>
            </SummaryList>
            <p style={{ margin: 0 }}>
              These inputs help our team assess fit and prepare the right consultation plan.
            </p>
          </Card>

          <Card id="custom-project-step-4" ref={setStepSectionRef(4)}>
            <h2 style={{ margin: 0 }}>Step 4: Submit request form</h2>
            <FormGrid onSubmit={onSubmit} noValidate>
              <Row>
                <Field>
                  Full name*
                  <Input
                    value={form.fullName}
                    onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                  />
                  {errors.fullName ? <ErrorText>{errors.fullName}</ErrorText> : null}
                </Field>
                <Field>
                  Business / company
                  <Input
                    value={form.company}
                    onChange={(event) => setForm((prev) => ({ ...prev, company: event.target.value }))}
                  />
                </Field>
              </Row>

              <Row>
                <Field>
                  Email*
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                  {errors.email ? <ErrorText>{errors.email}</ErrorText> : null}
                </Field>
                <Field>
                  Phone
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                  />
                  {errors.phone ? <ErrorText>{errors.phone}</ErrorText> : null}
                </Field>
              </Row>

              <Field>
                Preferred contact
                <PreferenceGrid role="radiogroup" aria-label="Preferred contact">
                  {CONTACT_PREFERENCES.map((option) => (
                    <PreferenceButton
                      key={option.id}
                      type="button"
                      $active={form.preferredContact === option.id}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          preferredContact: option.id,
                        }))
                      }
                      role="radio"
                      aria-checked={form.preferredContact === option.id}
                    >
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </PreferenceButton>
                  ))}
                </PreferenceGrid>
              </Field>

              <Field>
                Request details*
                <Textarea
                  value={form.requestDetails}
                  onChange={(event) => setForm((prev) => ({ ...prev, requestDetails: event.target.value }))}
                  placeholder="Describe goals, requirements, constraints, and anything specific your team needs."
                />
                {errors.requestDetails ? <ErrorText>{errors.requestDetails}</ErrorText> : null}
              </Field>

              {submitted ? (
                <SuccessText>
                  Request drafted. If your email app did not open, use the fallback button below.
                </SuccessText>
              ) : null}

              <ActionRow>
                <PrimaryButton type="submit">Submit custom request</PrimaryButton>
              </ActionRow>
            </FormGrid>

            <MobileOnly>
              <strong>View Other Services</strong>
              <GhostLink to="/services/platform-development">Platform Development</GhostLink>
              <GhostLink to="/services/managed-marketing">Managed Marketing</GhostLink>
              <GhostLink to="/services/assets-design">Assets/Design</GhostLink>
            </MobileOnly>
          </Card>
        </div>

        <RightColumn>
          <Card>
            <h3 style={{ margin: 0 }}>Selection Snapshot</h3>
            <SummaryList>
              <li>Project: {selectedProjectType.label}</li>
              <li>Timeline: {selectedTimeline.label}</li>
              <li>Engagement: {selectedEngagement.label}</li>
              <li>Preferred contact: {preferredContactLabel(form.preferredContact)}</li>
            </SummaryList>
            <PrimaryButton type="button" onClick={() => scrollToStep(4)}>
              Go to form
            </PrimaryButton>
          </Card>

          <Card>
            <strong>View Other Services</strong>
            <GhostLink to="/services/platform-development">Platform Development</GhostLink>
            <GhostLink to="/services/managed-marketing">Managed Marketing</GhostLink>
            <GhostLink to="/services/assets-design">Assets/Design</GhostLink>
          </Card>
        </RightColumn>

        <MobileSnapshotFab title="Selection Snapshot" buttonLabel="Snapshot">
          <Card style={{ minHeight: 'auto' }}>
            <h3 style={{ margin: 0 }}>Selection Snapshot</h3>
            <SummaryList>
              <li>Project: {selectedProjectType.label}</li>
              <li>Timeline: {selectedTimeline.label}</li>
              <li>Engagement: {selectedEngagement.label}</li>
              <li>Preferred contact: {preferredContactLabel(form.preferredContact)}</li>
            </SummaryList>
            <PrimaryButton type="button" onClick={() => scrollToStep(4)}>
              Go to form
            </PrimaryButton>
          </Card>

          <Card style={{ minHeight: 'auto' }}>
            <strong>View Other Services</strong>
            <GhostLink to="/services/platform-development">Platform Development</GhostLink>
            <GhostLink to="/services/managed-marketing">Managed Marketing</GhostLink>
            <GhostLink to="/services/assets-design">Assets/Design</GhostLink>
          </Card>
        </MobileSnapshotFab>
      </Layout>
    </FunnelShell>
  );
}
